import CreateIcon from '@mui/icons-material/Create';
import {
  Box,
  Button,
  Collapse,
  Fade,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';

import ProcessColorPicker from './process-color-picker.jsx';
import {setProcessColor} from '@explorer/actions/tiles';
import {trackEvent} from '@explorer/analytics';
import {useGetTilesCapabilitiesQuery} from '@explorer/api/apiSlice';
import {SPECTRAL_INDICES_KEY} from '@explorer/constants';
import {
  PROCESSES,
  PROCESS_CATEGORIES,
  PROCESS_SPECTRAL_INDICES,
} from '@explorer/constants/processes';
import {get as getStored, set as setStored} from '@explorer/util/store';

const RangeLabel = props => (
  <Typography
    variant="body2"
    align="center"
    sx={{minWidth: 16, alignSelf: 'flex-end', padding: 0.5}}
    {...props}
  />
);

const LegendLabel = props => (
  <Box
    sx={{
      padding: 0.5,
      whiteSpace: 'nowrap',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
    {...props}
  />
);

const ButtonToolbar = props => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: 0.5,
    }}
    {...props}
  />
);

const Legend = ({process, processColor, setProcessColor}) => {
  const isSpectralIndexProcess =
    PROCESS_CATEGORIES[PROCESS_SPECTRAL_INDICES].includes(process);
  // the first param is undefined to match other calls which simply don't set a param. RTK Query will treat any other value as a new query, triggering a new request
  const {data, isError} = useGetTilesCapabilitiesQuery(undefined, {
    skip: !isSpectralIndexProcess,
  });
  const [showEdit, setShowEdit] = useState(false);
  const meta = data?.indices[process];
  const isIndex = meta !== undefined;
  const colors = data?.colors;
  const inputName = 'color-ramp';

  const activeColor = useMemo(() => {
    const processDefaultColor = meta?.color;
    if (!meta?.['pref-colors'] && !colors && !processDefaultColor) {
      return '';
    }
    if (
      colors?.[processColor] ||
      meta?.['pref-colors']?.includes(processColor)
    ) {
      return processColor;
    }
    return processDefaultColor;
  }, [meta, colors, processColor]);
  const {t} = useTranslation();

  if (!isIndex || isError) {
    return false;
  }

  const getSpectralIndexColors = () => {
    if (!meta['pref-colors'] || !colors) {
      return {};
    }

    return meta['pref-colors'].length > 0
      ? meta['pref-colors'].reduce((acc, color) => {
          return {...acc, [color]: colors[color]};
        }, {})
      : colors;
  };

  const handleSubmit = e => {
    e.preventDefault();
    const selectedColorRamp = new FormData(e.target).get(inputName);
    const localStore = getStored(SPECTRAL_INDICES_KEY) || {};

    setStored(SPECTRAL_INDICES_KEY, {
      ...localStore,
      [process]: selectedColorRamp,
    });
    setProcessColor(selectedColorRamp);
    setShowEdit(false);
    trackEvent('color-ramp: set to custom ramp', {
      process,
      processColor,
    });
  };

  return (
    <Paper
      data-qe="legend"
      sx={{
        padding: theme => theme.spacing(0.5, 1),
        position: 'relative',
        zIndex: theme => theme.zIndex.drawer - 1,
      }}
      className="pl-mode-dark"
    >
      <LegendLabel>
        <Typography
          variant="subtitle2"
          align="center"
          noWrap
          title={PROCESSES[process].label}
        >
          {PROCESSES[process].label}
        </Typography>
        <Fade in={!showEdit}>
          <div>
            <Tooltip
              title={t('processColorPicker.changeColorButton')}
              placement="top"
            >
              <IconButton
                color="inherit"
                size="small"
                sx={{
                  marginLeft: 2,
                  borderRadius: 0,
                }}
                onClick={() => {
                  trackEvent('color-ramp: edit clicked', {
                    process,
                    processColor: activeColor,
                  });
                  setShowEdit(true);
                }}
                aria-expanded={showEdit}
              >
                <CreateIcon
                  fontSize="inherit"
                  sx={{
                    padding: 0.25,
                    border: 'solid 1px',
                  }}
                />
              </IconButton>
            </Tooltip>
          </div>
        </Fade>
      </LegendLabel>
      <Collapse in={showEdit}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <Typography
            color="inherit"
            id={`color-ramp-${activeColor}`}
            variant="body2"
          >
            {t('processColorPicker.selectColorLabel')}
          </Typography>
          <form
            autoComplete="off"
            id={`form-${process}`}
            noValidate
            onSubmit={handleSubmit}
          >
            <ProcessColorPicker
              colorMap={getSpectralIndexColors()}
              inputName={inputName}
              labelledby={`color-ramp-${activeColor}`}
              processColor={activeColor}
            />
          </form>
        </Box>
        <ButtonToolbar>
          <Button
            color="primary"
            onClick={() => {
              trackEvent('color-ramp: set to default', {
                process,
                processColor: meta?.color,
              });
              setProcessColor(meta?.color);
              setShowEdit(false);
            }}
            size="small"
            variant="text"
            type="button"
            sx={{
              marginRight: 1,
            }}
          >
            {t('processColorPicker.useDefaultButton')}
          </Button>
          <Button
            color="primary"
            form={`form-${process}`}
            size="small"
            type="submit"
            variant="contained"
            sx={{
              minWidth: 75,
            }}
          >
            {t('processColorPicker.saveButton')}
          </Button>
        </ButtonToolbar>
      </Collapse>
      <Collapse in={!showEdit}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <RangeLabel>{meta?.range?.[0]}</RangeLabel>
          <Box
            sx={{
              padding: 0.5,
              minHeight: '1.5em',
              width: '100%',
            }}
          >
            <img
              alt={t('processColorPicker.imageAlt', {color: activeColor})}
              height={10}
              src={colors[activeColor]?.icon}
              width="100%"
              draggable="false"
            />
          </Box>
          <RangeLabel>{meta?.range?.[1]}</RangeLabel>
        </Box>
      </Collapse>
    </Paper>
  );
};

Legend.propTypes = {
  process: PropTypes.string,
  processColor: PropTypes.string,
  setProcessColor: PropTypes.func,
};

const mapStateToProps = state => ({
  capabilities: state.tiles.capabilities,
  process: state.tiles.modeSettings[state.tiles.mode].process,
  processColor: state.tiles.modeSettings[state.tiles.mode].processColor,
});

const mapDispatchToProps = {
  setProcessColor,
};

Legend.displayName = 'Legend';

export default connect(mapStateToProps, mapDispatchToProps)(Legend);
